import type { FC, ReactNode } from 'react'
import type { WorkItem } from '@/types'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import PhoneFrame from '@/components/landing/phone-frame'

interface WorkCardProps {
    item: WorkItem
}

const WorkCard: FC<WorkCardProps> = ({ item }): ReactNode => {
    return (
        <article className='group flex flex-col'>
            <Link href={`/work/${item.slug}`} tabIndex={-1} aria-hidden='true' className='block'>
                <PhoneFrame
                    image={item.thumbnail}
                    alt={item.thumbnail_alt}
                    video={item.video}
                    playOnHover
                    className='transition-transform duration-500 ease-out group-hover:-translate-y-1.5 group-hover:rotate-[-1deg] motion-reduce:transition-none motion-reduce:group-hover:translate-y-0 motion-reduce:group-hover:rotate-0'
                >
                    <p className='text-[0.7rem] font-semibold opacity-90'>
                        @<span data-mdlfy-cms={`work:${item.id}:brand`}>{item.brand}</span>
                        <span className='ml-1.5 rounded bg-bone/20 px-1 py-px text-[0.6rem] font-medium'>Sponsored</span>
                    </p>
                    {item.hook && (
                        <p className='mt-1.5 text-sm leading-snug font-semibold [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]'>
                            <span data-mdlfy-cms={`work:${item.id}:hook`}>{item.hook}</span>
                        </p>
                    )}
                </PhoneFrame>
            </Link>

            <div className='mt-5 flex items-start justify-between gap-4'>
                <div className='min-w-0'>
                    <p className='flex flex-wrap items-center gap-x-2 font-mono text-[0.7rem] uppercase tracking-[0.15em] text-ink/55'>
                        {item.category && <span data-mdlfy-cms={`work:${item.id}:category`}>{item.category}</span>}
                        {item.category && item.format && <span aria-hidden='true'>/</span>}
                        {item.format && <span data-mdlfy-cms={`work:${item.id}:format`}>{item.format}</span>}
                    </p>
                    <h3 className='mt-2 font-heading text-2xl leading-tight tracking-tight text-ink'>
                        <Link
                            href={`/work/${item.slug}`}
                            className='rounded-sm hover:underline hover:decoration-signal hover:decoration-2 hover:underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-signal'
                        >
                            <span data-mdlfy-cms={`work:${item.id}:title`}>{item.title}</span>
                        </Link>
                    </h3>
                </div>
                <span className='mt-1 inline-flex size-9 shrink-0 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors group-hover:border-signal group-hover:bg-signal' aria-hidden='true'>
                    <ArrowUpRight className='size-4' />
                </span>
            </div>

            {item.metric_1_value && (
                <p className='mt-3 flex items-baseline gap-2 border-t border-ink/10 pt-3 text-sm text-ink/65'>
                    <span className='font-heading text-2xl text-ink' data-mdlfy-cms={`work:${item.id}:metric_1_value`}>{item.metric_1_value}</span>
                    {item.metric_1_label && <span data-mdlfy-cms={`work:${item.id}:metric_1_label`}>{item.metric_1_label}</span>}
                </p>
            )}
        </article>
    )
}

export default WorkCard
